import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { publicParentApi, setStoredParentSession } from "@/lib/parentApi";
import { useLanguage } from "@/context/LanguageContext";

const LOGO_URL = "/images/ym6gev0b_AE8ECFCA-9BBA-44D9-A534-DED3210EC5A4.png";

export default function ParentResetPassword() {
  const { t } = useLanguage();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (pw1 !== pw2) {
      toast.error(t("parent.pwMismatch"));
      return;
    }
    if (pw1.length < 8) {
      toast.error(t("parent.pwTooShort"));
      return;
    }
    if (!token) {
      toast.error(t("parent.missingToken"));
      return;
    }
    setLoading(true);
    try {
      const { data } = await publicParentApi.post("/parent/reset-password", { token, new_password: pw1 });
      setStoredParentSession(data.token, data.email);
      toast.success(t("parent.pwUpdated"));
      navigate("/parent/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-burns-black flex items-center justify-center p-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <img src={LOGO_URL} alt="Burns Elite Baseball" className="h-32 sm:h-36 mx-auto mb-4" />
          <h1 className="text-2xl font-heading font-bold text-white">{t("parent.resetTitle")}</h1>
        </div>

        {!token ? (
          <div className="text-center space-y-3">
            <p className="text-zinc-300">{t("parent.tokenInvalid")}</p>
            <Link to="/parent/forgot-password" className="text-burns-gold hover:underline">{t("parent.requestNewLink")}</Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="pw1" className="text-white">{t("parent.newPassword")}</Label>
              <div className="relative">
                <Input id="pw1" name="new-password" autoComplete="new-password" type={show ? "text" : "password"} required minLength={8} value={pw1} onChange={(e) => setPw1(e.target.value)} className="bg-zinc-800 border-zinc-700 text-white pr-10" placeholder={t("parent.passwordHint")} />
                <button type="button" onClick={() => setShow((s) => !s)} className="absolute inset-y-0 right-2 flex items-center text-zinc-500 hover:text-zinc-300" tabIndex={-1}>
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="pw2" className="text-white">{t("parent.confirmPassword")}</Label>
              <Input id="pw2" name="new-password-confirm" autoComplete="new-password" type={show ? "text" : "password"} required minLength={8} value={pw2} onChange={(e) => setPw2(e.target.value)} className="bg-zinc-800 border-zinc-700 text-white" />
            </div>
            <Button type="submit" disabled={loading || !pw1 || !pw2} className="w-full bg-burns-red hover:bg-burns-red/80 text-white">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {t("parent.updatePassword")}
            </Button>
            <div className="flex justify-between text-sm pt-2">
              <Link to="/parent/login" className="text-zinc-400 hover:text-white">{t("parent.backToLogin")}</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
